import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { parseNumber } from "../../utils/parseNumber";

export const useActions = ({
    workspace,
    confirmDisable,
    updateMaxCpc,
    enforceMaxCPCTargetImpressionShare,
    enforcePortfolioStrategy,
    disableKoBiddingManualMaxCpc,
    disableKoBiddingAutoMaxCpc,
    setLocalPatches,
    setModifiedRows,
    setConfirmDisable,
    updateKOBiddingAutoMaxCpc,
    data,
    refetch
}: any) => {

    const handleConfirmDisable = useCallback(
        async (row: any) => {

            if (!row) return;

            const modeToDisable =
                confirmDisable.mode;

            try {

                if (
                    modeToDisable ===
                    "KO Bidding Manual"
                ) {

                    const detail =
                        row.activations_details?.find(
                            (d: any) =>
                                d.mode ===
                                "KO Bidding Manual"
                        );

                    if (detail?.id) {

                        await disableKoBiddingManualMaxCpc({
                            id: detail.id
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            {
                                variant: "success"
                            }
                        );

                        setLocalPatches((prev: any) => {

                            const existing =
                                prev[row.id] ?? row;

                            return {
                                ...prev,

                                [row.id]: {
                                    ...existing,

                                    activations_details:
                                        existing.activations_details.filter(
                                            (d: any) =>
                                                d.mode !==
                                                "KO Bidding Manual"
                                        ),

                                    max_cpc: null,
                                    max_cpc_portfolio_strategy: null,
                                    target_cpa: null,
                                    target_cpa_portfolio_strategy: null,
                                    target_roas: null,
                                    target_roas_portfolio_strategy: null,
                                    target_impression_share: null,
                                    target_impression_share_position: null,
                                }
                            };
                        });
                    }
                }

                if (
                    modeToDisable?.startsWith(
                        "KO Bidding Auto"
                    )
                ) {

                    const detail =
                        row.activations_details?.find(
                            (d: any) =>
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                )
                        );

                    if (detail?.id) {

                        await disableKoBiddingAutoMaxCpc({
                            id: detail.id
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            {
                                variant: "success"
                            }
                        );

                        setLocalPatches((prev: any) => {

                            const existing =
                                prev[row.id] ?? row;

                            return {
                                ...prev,

                                [row.id]: {
                                    ...existing,

                                    activations_details:
                                        existing.activations_details.filter(
                                            (d: any) =>
                                                !d.mode?.startsWith(
                                                    "KO Bidding Auto"
                                                )
                                        ),
                                }
                            };
                        });
                    }
                }

                setConfirmDisable({
                    open: false,
                    row: null,
                    mode: null
                });

            } catch (err) {

                enqueueSnackbar(
                    `Failed to disable ${modeToDisable}`,
                    {
                        variant: "error"
                    }
                );
            }
        },
        [
            confirmDisable,
            setConfirmDisable,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches
        ]
    );

    const handleUpdateMaxCPC = useCallback(
        async (modifiedRows: any[]) => {
            try {
                const manualRows = modifiedRows.filter((row) => {
                    const original = data.find(
                        (r: any) => r.id === row.rowId
                    );

                    return original?.activations_details?.some(
                        (a: any) =>
                            a.mode === "KO Bidding Manual" &&
                            a.status === true
                    );
                });

                const autoRows = modifiedRows.filter((row) => {
                    const original = data.find(
                        (r: any) => r.id === row.rowId
                    );

                    return original?.activations_details?.some(
                        (a: any) =>
                            a.mode?.startsWith("KO Bidding Auto") &&
                            a.status === true
                    );
                });

                if (manualRows.length > 0) {

                    const payload = manualRows.map((row) => ({
                        id: row.id,
                        workspaceId: workspace?.id,

                        max_cpc: Number(row.max_cpc),
                        max_cpc_portfolio_strategy:
                            Number(row.max_cpc_portfolio_strategy),

                        target_cpa: Number(row.target_cpa),

                        target_cpa_portfolio_strategy:
                            Number(row.target_cpa_portfolio_strategy),

                        target_roas: Number(row.target_roas),

                        target_roas_portfolio_strategy:
                            Number(row.target_roas_portfolio_strategy),

                        target_impression_share:
                            Number(row?.target_impression_share),

                        target_impression_share_position:
                            row?.target_impression_share_position,
                    }));

                    await updateMaxCpc(payload).unwrap();
                }

                if (autoRows.length > 0) {
                    const autoPayload = autoRows.map((row) => ({
                        id: row.id,
                        workspaceId: workspace?.id,

                        target_roas_portfolio_strategy:
                            Number(row.target_roas_portfolio_strategy),
                    }));

                    await updateKOBiddingAutoMaxCpc(
                        autoPayload
                    ).unwrap();
                }

                await refetch();

                setModifiedRows([]);

                enqueueSnackbar(
                    `Updates applied successfully`,
                    { variant: "success" }
                );

            } catch (error) {
                setLocalPatches({});
                setModifiedRows([]);
            }
        },
        [
            data,
            refetch,
            workspace?.id,
            updateMaxCpc,
            updateKOBiddingAutoMaxCpc,
            setLocalPatches,
            setModifiedRows,
        ]
    );

    const handleFinish = useCallback(
        async (rows: any, setResponse: any) => {

            const buildPayload = (rowsToMap: any[]) => ({
                campaigns: rowsToMap.map((row: any) => {

                    const activeActivation =
                        row.activations_details?.find(
                            (detail: any) => detail.status === true
                        );

                    return {
                        account_id: String(row.account_id),

                        campaign_id: String(
                            activeActivation?.campaign_id?.[0]
                            ?? row.campaign_id
                        ),

                        max_bid: row.max_cpc_to_enforce,

                        max_cpc_portfolio_strategy:
                            row.max_cpc_to_enforce,

                        target_percentage:
                            activeActivation?.target_impression_share,

                        position:
                            activeActivation?.target_impression_share_position,

                        bidding_strategy_type:
                            row.bidding_strategy_type,

                        platform:
                            activeActivation?.platform
                            ?? (
                                row?.platform === "GOOGLE"
                                    ? "Google Ads"
                                    : row?.platform === "BING"
                                        ? "Bing Ads"
                                        : row?.platform
                            )
                    };
                })
            });

            try {

                const targetImpressionShareRows = rows.filter(
                    (row: any) =>
                        row.bidding_strategy_type ===
                        "Target Impression Share"
                );

                const portfolioRows = rows.filter(
                    (row: any) =>
                        row.bidding_strategy_type !==
                        "Target Impression Share"
                );

                let responses: any[] = [];

                if (targetImpressionShareRows.length > 0) {
                    const response =
                        await enforceMaxCPCTargetImpressionShare(
                            buildPayload(targetImpressionShareRows)
                        ).unwrap();

                    responses = [...responses, ...response];
                }

                if (portfolioRows.length > 0) {
                    const response =
                        await enforcePortfolioStrategy(
                            buildPayload(portfolioRows)
                        ).unwrap();

                    responses = [...responses, ...response];
                }

                setResponse(responses);

            } catch (error) {
                console.error("Enforce error", error);
            }
        },
        [
            enforceMaxCPCTargetImpressionShare,
            enforcePortfolioStrategy
        ]
    );

    const handleRowUpdate = useCallback(
        (newRow: any, oldRow: any) => {

            // =====================================================
            // KO BIDDING MANUAL
            // =====================================================
            const koManual = newRow.activations_details?.find(
                (d: any) =>
                    d.mode === "KO Bidding Manual" &&
                    d.status === true
            );

            // =====================================================
            // KO BIDDING AUTO
            // =====================================================
            const koAuto = newRow.activations_details?.find(
                (d: any) =>
                    d.mode?.startsWith("KO Bidding Auto") &&
                    d.status === true
            );

            // =====================================================
            // ACTIVE MODE
            // =====================================================
            const activeActivation = koManual || koAuto;

            const isManualMode = !!koManual;
            const isAutoMode = !!koAuto;

            const isInvalidNumber = (v: number | null) =>
                v === null || Number.isNaN(v) || v < 0.01;

            // =====================================================
            // MAX CPC
            // =====================================================
            const nextMaxCpc = parseNumber(newRow.max_cpc);
            const oldMaxCpc = parseNumber(oldRow.max_cpc);

            const maxCpcChanged =
                nextMaxCpc !== oldMaxCpc;

            // =====================================================
            // TARGET CPA
            // =====================================================
            const nextTargetCpa =
                parseNumber(newRow.target_cpa);

            const oldTargetCpa =
                parseNumber(oldRow.target_cpa);

            const targetCpaChanged =
                nextTargetCpa !== oldTargetCpa;

            // =====================================================
            // TARGET ROAS
            // =====================================================
            const nextTargetRoas =
                parseNumber(newRow.target_roas);

            const oldTargetRoas =
                parseNumber(oldRow.target_roas);

            const targetRoasChanged =
                nextTargetRoas !== oldTargetRoas;

            // =====================================================
            // TARGET IMPRESSION SHARE
            // =====================================================
            const nextTargetIS =
                parseNumber(newRow.target_impression_share);

            const oldTargetIS =
                parseNumber(oldRow.target_impression_share);

            const targetISChanged =
                nextTargetIS !== oldTargetIS;

            // =====================================================
            // TARGET IMPRESSION SHARE POSITION
            // =====================================================
            const nextTargetISPosition =
                newRow.target_impression_share_position;

            const oldTargetISPosition =
                oldRow.target_impression_share_position;

            const targetISPositionChanged =
                nextTargetISPosition !== oldTargetISPosition;

            // =====================================================
            // NO CHANGE
            // =====================================================
            if (
                !maxCpcChanged &&
                !targetCpaChanged &&
                !targetRoasChanged &&
                !targetISChanged &&
                !targetISPositionChanged
            ) {
                return oldRow;
            }

            // =====================================================
            // VALIDATION
            // =====================================================
            const isInvalidMaxCpc =
                activeActivation &&
                maxCpcChanged &&
                isInvalidNumber(nextMaxCpc);

            const isInvalidTargetCpa =
                activeActivation &&
                targetCpaChanged &&
                isInvalidNumber(nextTargetCpa);

            const isInvalidTargetRoas =
                activeActivation &&
                targetRoasChanged &&
                isInvalidNumber(nextTargetRoas);

            const isInvalidTargetIS =
                activeActivation &&
                targetISChanged &&
                isInvalidNumber(nextTargetIS);

            const isInvalidTargetISPosition =
                activeActivation &&
                targetISPositionChanged &&
                !nextTargetISPosition;

            const isInvalid =
                isInvalidMaxCpc ||
                isInvalidTargetCpa ||
                isInvalidTargetRoas ||
                isInvalidTargetIS ||
                isInvalidTargetISPosition;

            // =====================================================
            // MODIFIED ROWS
            // =====================================================
            setModifiedRows((prev: any) => {

                const filtered = prev.filter(
                    (r: any) => r.rowId !== newRow.id
                );

                const existing = prev.find(
                    (r: any) => r.rowId === newRow.id
                );

                if (isInvalid) {
                    return filtered;
                }

                const patch: any = {
                    ...existing,

                    rowId: newRow.id,

                    // 🔥 IMPORTANT
                    // MANUAL => koManual.id
                    // AUTO => koAuto.id
                    id: isManualMode
                        ? koManual?.id
                        : koAuto?.id,
                };

                // =====================================================
                // MAX CPC
                // =====================================================
                if (maxCpcChanged) {

                    const isPortfolio =
                        activeActivation?.max_cpc == null;

                    const key = isPortfolio
                        ? "max_cpc_portfolio_strategy"
                        : "max_cpc";

                    patch[key] = nextMaxCpc;
                }

                // =====================================================
                // TARGET CPA
                // =====================================================
                if (targetCpaChanged) {

                    const isPortfolio =
                        activeActivation?.target_cpa == null;

                    const key = isPortfolio
                        ? "target_cpa_portfolio_strategy"
                        : "target_cpa";

                    patch[key] = nextTargetCpa;
                }

                // =====================================================
                // TARGET ROAS
                // =====================================================
                if (targetRoasChanged) {

                    // =========================
                    // KO BIDDING AUTO
                    // =========================
                    if (isAutoMode) {

                        patch["target_roas_portfolio_strategy"] =
                            nextTargetRoas;
                    }

                    // =========================
                    // KO BIDDING MANUAL
                    // =========================
                    else {

                        const isPortfolio =
                            koManual?.target_roas == null;

                        const key = isPortfolio
                            ? "target_roas_portfolio_strategy"
                            : "target_roas";

                        patch[key] = nextTargetRoas;
                    }
                }

                // =====================================================
                // TARGET IS
                // =====================================================
                if (targetISChanged) {

                    patch["target_impression_share"] =
                        nextTargetIS;
                }

                // =====================================================
                // TARGET IS POSITION
                // =====================================================
                if (targetISPositionChanged) {

                    patch["target_impression_share_position"] =
                        nextTargetISPosition;
                }

                return [...filtered, patch];
            });

            // =====================================================
            // UPDATED ROW
            // =====================================================
            const updatedRow = {
                ...newRow,

                activations_details:
                    newRow.activations_details?.map(
                        (a: any) => {

                            if (
                                isManualMode &&
                                a.id !== koManual?.id
                            ) {
                                return a;
                            }

                            if (
                                isAutoMode &&
                                a.id !== koAuto?.id
                            ) {
                                return a;
                            }

                            const isPortfolioMax =
                                a.max_cpc == null;

                            const isPortfolioCpa =
                                a.target_cpa == null;

                            const isPortfolioRoas =
                                a.target_roas == null;

                            return {
                                ...a,

                                ...(maxCpcChanged && {
                                    max_cpc:
                                        !isPortfolioMax
                                            ? nextMaxCpc
                                            : a.max_cpc,

                                    max_cpc_portfolio_strategy:
                                        isPortfolioMax
                                            ? nextMaxCpc
                                            : a.max_cpc_portfolio_strategy,
                                }),

                                ...(targetCpaChanged && {
                                    target_cpa:
                                        !isPortfolioCpa
                                            ? nextTargetCpa
                                            : a.target_cpa,

                                    target_cpa_portfolio_strategy:
                                        isPortfolioCpa
                                            ? nextTargetCpa
                                            : a.target_cpa_portfolio_strategy,
                                }),

                                ...(targetRoasChanged && (

                                    isAutoMode
                                        ? {
                                            target_roas_portfolio_strategy:
                                                nextTargetRoas,
                                        }

                                        : {
                                            target_roas:
                                                !isPortfolioRoas
                                                    ? nextTargetRoas
                                                    : a.target_roas,

                                            target_roas_portfolio_strategy:
                                                isPortfolioRoas
                                                    ? nextTargetRoas
                                                    : a.target_roas_portfolio_strategy,
                                        }
                                )),

                                ...(targetISChanged && {
                                    target_impression_share:
                                        nextTargetIS,
                                }),

                                ...(targetISPositionChanged && {
                                    target_impression_share_position:
                                        nextTargetISPosition,
                                }),
                            };
                        }
                    ),

                isEdited: true,
                isInvalid,
            };

            return updatedRow;
        },
        [setModifiedRows]
    );

    return {
        handleConfirmDisable,
        handleFinish,
        handleUpdateMaxCPC,
        handleRowUpdate
    };
};